import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * useLiveLocationSharing(bookingId) — hook that lets the customer share their location.
 * Persists the on/off state in localStorage so it survives page reloads.
 * Pings backend every 30s while active.
 */
export default function useLiveLocationSharing(bookingId) {
  const storageKey = bookingId ? `share_loc_${bookingId}` : null;
  const [sharing, setSharing] = useState(() => storageKey ? localStorage.getItem(storageKey) === "1" : false);
  const [lastPing, setLastPing] = useState(null);
  const [error, setError] = useState(null);
  const watchIdRef = useRef(null);
  const lastSentRef = useRef(0);

  const sendPing = useCallback(async (pos) => {
    try {
      await api.post(`/bookings/${bookingId}/gps-ping`, {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        speed: pos.coords.speed,
      });
      setLastPing(new Date().toISOString());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send location");
    }
  }, [bookingId]);

  const stop = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setSharing(false);
    if (storageKey) localStorage.removeItem(storageKey);
  }, [storageKey]);

  const start = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError("Geolocation is not supported by your browser");
      return;
    }
    setSharing(true);
    if (storageKey) localStorage.setItem(storageKey, "1");
    // initial ping
    navigator.geolocation.getCurrentPosition(
      (pos) => { lastSentRef.current = Date.now(); sendPing(pos); },
      (err) => setError(err.message || "Location permission denied"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
    // continuous watch — throttle to 30s
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSentRef.current >= 30000) {
          lastSentRef.current = now;
          sendPing(pos);
        }
      },
      (err) => setError(err.message),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 },
    );
  }, [sendPing, storageKey]);

  // Resume on mount if previously sharing
  useEffect(() => {
    if (sharing && watchIdRef.current == null) {
      start();
    }
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { sharing, start, stop, lastPing, error };
}
